
import { useEffect, useState } from 'react';
import { useTokens } from './useTokens';
import { useToast } from './use-toast';

interface UseTokenWarningsProps {
  onTokenDepletionModal?: () => void;
}

export const useTokenWarnings = ({ onTokenDepletionModal }: UseTokenWarningsProps = {}) => {
  const { tokenBalance, hasEnoughTokens, isLowBalance, isInitialized, isTokenDepleted } = useTokens();
  const { toast } = useToast();
  const [hasShownLowBalanceWarning, setHasShownLowBalanceWarning] = useState(false);
  const [hasTriggeredDepletionModal, setHasTriggeredDepletionModal] = useState(false);

  // Show low balance warning toast (once per session) - but not the modal
  useEffect(() => {
    if (isInitialized && isLowBalance() && !hasShownLowBalanceWarning && tokenBalance > 0) {
      toast({
        title: "Token Balance Low",
        description: `You have ${tokenBalance.toLocaleString()} tokens remaining. Consider purchasing more to continue using RemoteViz.`,
        variant: "default",
      });
      setHasShownLowBalanceWarning(true);
    }
  }, [tokenBalance, isLowBalance, hasShownLowBalanceWarning, toast, isInitialized]);

  // FIXED: Only trigger depletion modal when initialized and tokens are actually depleted
  useEffect(() => {
    if (isInitialized && isTokenDepleted() && !hasTriggeredDepletionModal && onTokenDepletionModal) {
      console.log('💰 useTokenWarnings: Triggering token depletion modal, balance:', tokenBalance);
      onTokenDepletionModal();
      setHasTriggeredDepletionModal(true);
    }
  }, [isTokenDepleted, hasTriggeredDepletionModal, onTokenDepletionModal, tokenBalance, isInitialized]);

  // Reset warnings when balance increases significantly
  useEffect(() => {
    if (tokenBalance > 500) {
      setHasShownLowBalanceWarning(false);
      setHasTriggeredDepletionModal(false);
    }
  }, [tokenBalance]);

  // FIXED: Provide more accurate insufficient tokens messages
  const getInsufficientTokensMessage = (requiredTokens: number = 500) => {
    if (isTokenDepleted()) {
      return "❌ **Out of Tokens**\n\nYou've run out of tokens and can't continue using RemoteViz. Please purchase more tokens to continue your remote viewing sessions.";
    }
    
    if (tokenBalance < requiredTokens) {
      return `❌ **Insufficient Tokens**\n\nYou have ${tokenBalance.toLocaleString()} tokens remaining, but need approximately ${requiredTokens.toLocaleString()} tokens for this request. Please purchase more tokens to continue.`;
    }

    return `❌ **Insufficient Tokens**\n\nThis request requires approximately ${requiredTokens.toLocaleString()} tokens, but you only have ${tokenBalance.toLocaleString()} remaining. Please purchase more tokens to continue.`;
  };

  const checkTokensForRequest = (estimatedTokens: number = 500): boolean => {
    return hasEnoughTokens(estimatedTokens);
  };

  return {
    tokenBalance,
    isTokenDepleted: isTokenDepleted(),
    isLowBalance: isLowBalance(),
    hasEnoughTokens,
    checkTokensForRequest,
    getInsufficientTokensMessage,
    isInitialized, // Expose initialization state
  };
};
